import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import { api } from '../services/api';

/**
 * "Ordering" checkout launcher for an admin-provisioned, pay-first (`pending`)
 * tenant. The pending gate in App.tsx redirects here after login; this page
 * immediately requests a Stripe Checkout session for the tenant's provisioned
 * plan and redirects the browser to it. On success Stripe returns to
 * /onboarding/brand/new (set server-side), so the flow is
 * email → login → checkout → first brand.
 *
 * If the backend can't resolve a price (409 — e.g. an enterprise created without
 * a contract amount), we surface a message rather than loop.
 */
export default function CheckoutLaunch() {
  const [error, setError] = useState<string | null>(null);
  const launched = useRef(false);

  const launch = async () => {
    setError(null);
    try {
      const res = await api.post<{ checkoutUrl: string }>('/billing/checkout/provisioned-order', {});
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }
      setError('Impossible de démarrer le paiement. Contactez-nous à contact@traaker.ai.');
    } catch (e: any) {
      setError(
        e?.body?.message ||
          e?.message ||
          'Impossible de démarrer le paiement. Contactez-nous à contact@traaker.ai.',
      );
    }
  };

  useEffect(() => {
    // Guard against React StrictMode double-invoke creating two Checkout sessions.
    if (launched.current) return;
    launched.current = true;
    launch();
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 3,
        textAlign: 'center',
      }}
    >
      {error ? (
        <>
          <Alert severity="error" sx={{ maxWidth: 480 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => {
              launched.current = true;
              launch();
            }}
          >
            Réessayer
          </Button>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Redirection vers le paiement sécurisé…
          </Typography>
        </>
      )}
    </Box>
  );
}
